// src/components/LicenseTermBanner.jsx
//
// Org-admin-only notice that a licence term is ending or has ended. In-flow
// banner, dismissible, never a modal — this is information, not a decision to
// force. Nothing here enforces anything; enforcement is MR 1's
// `aiprime.org_holds_tier` + capability.js, per request at the gate.
//
// ── EVERY MESSAGE NAMES ITS TIER, AND THAT IS A CONSTRAINT ──────────────────
//
// The payload's scalars describe the org's MOST URGENT tier, while
// `term_end_earliest` is the earliest still-live term_end across ALL tiers.
// On a multi-tier org those are routinely different tiers. Measured: ANALYST
// live to 2026-10-15 beside ENTERPRISE_SITE in grace summarises as
// term_state=grace, days_remaining=7, term_end_earliest=2026-10-15.
//
// "Your licence is in grace, expires 2026-10-15" is two facts about two tiers
// glued into one false sentence. So the server sends `term_state_tier_name`
// and every message below names it: "Enterprise Site is in grace, 7 days
// remaining". Do not add a message that omits the tier, and do not render
// `term_end_earliest` in the same sentence as `days_remaining`.
//
// ── WHAT IT DOES NOT COMPUTE ────────────────────────────────────────────────
//
// No dates are compared here. `term_state`, `days_remaining`, `assignable` and
// `access_held` all arrive decided — `assignable` and `access_held` come
// straight from `org_holds_tier`, called twice server-side. Migration 061
// exists because that rule had two homes and now has one; re-deriving it here
// against the browser's clock would make three, and the browser's clock is not
// the one enforcement uses.
//
// ── IN FLOW, LIKE ImpersonationBanner ──────────────────────────────────────
//
// Not `fixed`. See that component's header for why. The two stack as ordinary
// siblings when a platform admin impersonates an org whose licence is lapsing;
// neither needs to know the other's height.
//
// ── DISMISSAL IS sessionStorage, DELIBERATELY ───────────────────────────────
//
// First use of sessionStorage in ui/src. It sidesteps the persistKeys contract
// in useAuthStore/sessionGuard on purpose: those govern localStorage, which is
// shared across tabs and survives restarts. A dismissed licence warning should
// come back — next session, next tab, next window. sessionStorage is per-tab
// and dies with it, which is exactly the lifetime this dismissal should have.
//
// sessionGuard.clearSessionState() clears the key anyway, so a logout inside
// one tab does not leave the next user looking at a banner the previous user
// dismissed.

import { useEffect, useState } from "react";
import { AlertTriangle, Clock, X } from "lucide-react";
import { useOrgStore } from "../stores/useOrgStore";
import { LICENSE_TIER_LABEL } from "../constants/licenseTiers";
import { fetchWithAuth } from "../api/fetchWithAuth";

// Exported so sessionGuard can clear it without duplicating the string.
export const LICENSE_BANNER_DISMISS_KEY = "aiprime.licenseBanner.dismissed";

// Warn this far ahead of a live term ending. Not a server value: it is a
// presentation choice about when a countdown becomes interesting, unlike the
// grace window, which is a rule and arrives in the payload as `grace_days`.
const WARN_WITHIN_DAYS = 30;

function dismissedKeyFor(state, tierCode) {
  // Keyed by state AND tier, so dismissing an expiry notice does not also
  // swallow the grace notice that follows it, and dismissing one tier's
  // warning does not hide another's.
  return `${LICENSE_BANNER_DISMISS_KEY}:${tierCode ?? "-"}:${state}`;
}

function readDismissed(key) {
  try { return window.sessionStorage.getItem(key) === "1"; } catch { return false; }
}

// Mounted in App.jsx, which has no licence payload of its own, so this fetches
// its own — the same self-sufficiency ImpersonationBanner has with its store.
// OrgAdminPage keeps its own fetch; that page needs the full payload for the
// seat tables and this needs six scalars, and coupling a global banner to one
// page's load order would mean the banner disappears whenever that page is not
// mounted.
export default function LicenseTermBanner() {
  const orgRole = useOrgStore((s) => s.role);
  const isOrgAdmin = orgRole === "org_admin";
  const [license, setLicense] = useState(null);
  // Bumped on dismiss purely to force one re-render; the dismissal itself is
  // read back out of sessionStorage below rather than mirrored in state.
  const [, setDismissTick] = useState(0);

  useEffect(() => {
    if (!isOrgAdmin) { setLicense(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth("/api/v1/org/license");
        if (!res.ok) return;                       // 403 for non-admins; stay silent
        const data = await res.json();
        if (!cancelled) setLicense(data);
      } catch {
        // A failed fetch renders nothing. It must NOT render a warning: an
        // unreachable server is not an expired licence, and guessing in that
        // direction would put a false lapse notice in front of every admin
        // during an outage.
      }
    })();
    return () => { cancelled = true; };
  }, [isOrgAdmin, orgRole]);

  // Org admins only. Everyone else has nothing to do about a licence term, and
  // a warning nobody can act on is the kind that teaches people to ignore
  // banners.
  if (!isOrgAdmin) return null;
  if (!license) return null;

  const {
    term_state: state,
    days_remaining: days,
    grace_days: graceDays,
    term_state_tier_code: tierCode,
    term_state_tier_name: tierName,
  } = license;

  // Tier label: the UI constant wins over the server's display_name, matching
  // OrgLicenseSummary so one tier is not called two things on one screen.
  const tier = LICENSE_TIER_LABEL[tierCode] ?? tierName ?? tierCode ?? "Your license";

  let tone = null;
  let text = null;

  if (state === "grace") {
    tone = "warn";
    text = days == null
      ? `${tier} has expired and is in its grace period. New seat assignments are paused.`
      : `${tier} has expired and is in its ${graceDays}-day grace period — `
        + `${days} day${days === 1 ? "" : "s"} remaining. New seat assignments are paused.`;
  } else if (state === "lapsed") {
    tone = "error";
    text = `${tier} has lapsed. Access to it has stopped and new seat assignments are paused.`;
  } else if (state === "live" && days != null && days <= WARN_WITHIN_DAYS) {
    tone = "info";
    text = `${tier} expires in ${days} day${days === 1 ? "" : "s"}.`;
  }

  // live beyond the window, live with no end date, pending, and none all
  // render nothing. PENDING IS SILENT ON PURPOSE: a contract that starts next
  // month is not a problem, and the state exists so the classifier does not
  // have to call it "lapsed", not so the banner can announce it.
  if (!text) return null;

  // Read straight from sessionStorage on every render rather than mirroring it
  // into state: the key depends on `state` and `tierCode`, which are not known
  // until after the hooks have run, so a useState initialiser cannot see it.
  const key = dismissedKeyFor(state, tierCode);
  if (readDismissed(key)) return null;

  function dismiss() {
    try { window.sessionStorage.setItem(key, "1"); } catch { /* private mode — banner simply stays */ }
    setDismissTick((t) => t + 1);
  }

  const palette = tone === "error"
    ? "bg-red-500/20 border-red-500/50 text-red-50"
    : tone === "warn"
      ? "bg-amber-500/25 border-amber-500/50 text-amber-50"
      : "bg-sky-500/20 border-sky-500/50 text-sky-50";

  const Icon = tone === "info" ? Clock : AlertTriangle;

  return (
    <div
      className={`shrink-0 border-b px-3 py-1
                  flex items-center justify-center gap-2 text-[11px] font-medium ${palette}`}
      role="status"
      aria-live="polite"
    >
      <Icon size={12} className="shrink-0" />
      <span>{text}</span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss license notice"
        className="ml-2 shrink-0 rounded p-0.5 opacity-70 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-white/40"
      >
        <X size={12} />
      </button>
    </div>
  );
}
